import { useEffect, useRef, useState } from "react";
import { getEmbedSDK } from "@looker/embed-sdk";
import gql from "graphql-tag";

import { Spinner } from "components/Spinner/Spinner";
import {
  AcquireLookerSession,
  GenerateLookerTokens,
} from "generated/copilot-api-developer";
import { useMutation } from "graphql-client/useMutation";
import { useTranslation } from "i18n";
import { notifier } from "utils/notifier";

gql`
  # schema = COPILOT_API_DEVELOPER

  mutation AcquireLookerSession($timezone: TimeZone!) {
    acquireLookerSession(timezone: $timezone) {
      authenticationToken
      apiToken
      apiTokenTtl
      navigationToken
      navigationTokenTtl
      sessionReferenceTokenTtl
    }
  }

  mutation GenerateLookerTokens($input: GenerateLookerTokensInput!) {
    generateLookerTokens(input: $input) {
      apiToken
      apiTokenTtl
      navigationToken
      navigationTokenTtl
      sessionReferenceTokenTtl
    }
  }
`;

type LoadingStatus = "loading" | "loaded" | "error";
type LookerTokens = {
  apiToken: string | null;
  navigationToken: string | null;
};

export const LookerDashboard = ({
  analyticsDashboardUrl,
}: {
  analyticsDashboardUrl: string;
}) => {
  const trans = useTranslation();
  const transRef = useRef(trans);
  useEffect(() => {
    transRef.current = trans;
  }, [trans]);

  const [status, setStatus] = useState<LoadingStatus>("loading");
  const [acquireLookerSession] = useMutation(AcquireLookerSession, {
    throwOnError: true,
  });
  const [generateLookerTokens] = useMutation(GenerateLookerTokens, {
    throwOnError: true,
  });
  const tokensRef = useRef<LookerTokens>({
    apiToken: null,
    navigationToken: null,
  });

  const dashboardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let isCancelled = false;
    const container = dashboardRef.current;
    const setLookerTokens = ({ apiToken, navigationToken }: LookerTokens) => {
      tokensRef.current = { apiToken, navigationToken };
    };

    const loadDashboard = async () => {
      setStatus("loading");
      try {
        const sdk = getEmbedSDK();
        sdk.initCookieless(
          new URL(analyticsDashboardUrl).hostname,
          async () => {
            const data = await acquireLookerSession({
              timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            });
            setLookerTokens({
              apiToken: data.apiToken,
              navigationToken: data.navigationToken,
            });

            return {
              authentication_token: data.authenticationToken,
              api_token: data.apiToken,
              api_token_ttl: data.apiTokenTtl,
              navigation_token: data.navigationToken,
              navigation_token_ttl: data.navigationTokenTtl,
              session_reference_token_ttl: data.sessionReferenceTokenTtl,
            };
          },
          async () => {
            const { apiToken, navigationToken } = tokensRef.current;
            if (!navigationToken) {
              throw new Error(
                "navigationToken is needed to generate new looker tokens",
              );
            }
            if (!apiToken) {
              throw new Error(
                "apiToken is needed to generate new looker tokens",
              );
            }

            const data = await generateLookerTokens({
              input: {
                apiToken,
                navigationToken,
              },
            });

            setLookerTokens({
              apiToken: data.apiToken,
              navigationToken: data.navigationToken,
            });

            return {
              api_token: data.apiToken,
              api_token_ttl: data.apiTokenTtl,
              navigation_token: data.navigationToken,
              navigation_token_ttl: data.navigationTokenTtl,
              session_reference_token_ttl: data.sessionReferenceTokenTtl,
            };
          },
        );
        if (!container) throw new Error("Dashboard container is not mounted");
        container.innerHTML = "";

        const client = sdk
          .createDashboardWithUrl(analyticsDashboardUrl)
          .appendTo(container)
          .withClassName("looker-embed")
          .withClassName("w-full")
          .withClassName("h-[900px]")
          .withClassName("border-0")
          .build();

        await client.connect();
        if (!isCancelled) setStatus("loaded");
      } catch (error) {
        if (!isCancelled) {
          setStatus("error");
          notifier.error({
            user: transRef.current("developers.analytics.error"),
            sentry: {
              exception: error,
            },
          });
        }
      }
    };

    void loadDashboard();

    return () => {
      isCancelled = true;
    };
  }, [
    acquireLookerSession,
    analyticsDashboardUrl,
    dashboardRef,
    generateLookerTokens,
  ]);

  return (
    <div className="relative flex-fill min-h-0">
      {status === "loading" && (
        <div className="absolute inset-0 z-10 flex-center bg-white/60">
          <Spinner />
        </div>
      )}
      {status === "error" && (
        <div className="absolute inset-0 z-10 flex-center text-grey-300 bg-white">
          {trans("developers.analytics.error")}
        </div>
      )}
      <div ref={dashboardRef} className="w-full h-full min-h-[900px]" />
    </div>
  );
};
